/* Where the water is, and how far below its surface each pixel sits.
 *
 * The presenter in `src/display.ts` works on the finished 256x240 frame and
 * has no idea what any pixel MEANS — which is fine for a bloom or a vignette
 * and useless for water, because a reflection has to know where the surface
 * is. Guessing from colour would be a shader that breaks the day somebody
 * changes a palette.
 *
 * So the game hands it a mask, built once per screen:
 *
 *   R = 255 where this pixel is water
 *   G = how many pixels below the water's surface it is, in that column,
 *       clamped to 255
 *
 * With those two the shader can mirror properly: the surface for a pixel at
 * depth d is d pixels above it, so what is reflected there is whatever the
 * frame holds 2d pixels up. That is a planar reflection, and it is correct
 * rather than a wobble that happens to look wet.
 */
import { T, TS } from './art.ts';
import { WET } from './passable.ts';



/** Build the mask for a screen.
 *
 *  THE SAME SHAPE AS THE FRAME, NOT AS THE PLAYFIELD. The shader samples the
 *  mask and the picture with one set of texture coordinates, so a mask that is
 *  256x192 against a 256x240 frame is stretched over the HUD as well —
 *  misaligning the water by the HUD's height AND letting the reflection sample
 *  the HUD, which put smeared readout text across the top of every lake. The
 *  mask is frame-shaped and the playfield is drawn into it at its real offset.
 *
 *  @param hudH  pixels of HUD above the playfield */
/** @param depthIn  for each pixel column, how deep the water already was at the
 *  bottom of the screen ABOVE this one — see the note in the walk below. */
export function buildWaterMask(tiles, cols, rows, hudH = 0, depthIn = null, kinds = null) {
  const W = cols * TS, H = rows * TS + hudH;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const g = c.getContext('2d');
  // A HEADLESS CONTEXT HAS NO PIXELS. The test harness draws through a stub
  // that answers every call and stores nothing, so `createImageData` comes
  // back undefined — and a mask is a presentation detail that nothing in the
  // simulation depends on. No pixels, no mask.
  const img = g.createImageData && g.createImageData(W, H);
  if (!img || !img.data) return null;
  const d = img.data;
  let any = false;
  for (let x = 0; x < W; x++) {
    const tx = Math.floor(x / TS);
    // walk down the column; depth resets every time we leave the water, so two
    // ponds stacked vertically each get their own surface rather than the
    // lower one reflecting from the upper one's rim
    /* ...AND THE COLUMN DOES NOT START AT THE TOP OF THE SCREEN.
     *
     * Depth was counted from the first water pixel in THIS screen, so a body
     * of water crossing a screen boundary had its depth reset there — and the
     * shader bands the water by depth, so the band stepped at every boundary.
     * Measured over ocean pixels on the composited island: a mean luma step of
     * 32.5 across a horizontal seam against a control of 9, and turning the
     * water shader off alone collapsed it to 6.3 against 6.5. Reported from
     * the map twice as seams in the sea.
     *
     * `depthIn` carries how deep the column already was at the bottom of the
     * screen above, so the count continues across the join. */
    let depth = depthIn ? depthIn[x] - 1 : -1;
    for (let py = 0; py < rows * TS; py++) {
      const y = py + hudH;
      const ty = Math.floor(py / TS);
      // the field's own kinds when the screen has them (LS-218), the cell's otherwise
      const kindAt = (px2, py2) => kinds ? kinds[py2 * (cols * TS) + px2] : tiles[Math.floor(py2 / TS)][Math.floor(px2 / TS)];
      // the field decides only near a shore -- a tile with a differently wet neighbour -- so the open
      // sea's depth walk is exactly the cell's, continuous across the screen joins as it was
      let shoreTile = false;
      if (kinds) { const w0 = WET.has(tiles[ty][tx]); for (let dy = -1; dy <= 1 && !shoreTile; dy++) for (let dx = -1; dx <= 1; dx++) { const nx = tx + dx, ny = ty + dy; if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue; if (WET.has(tiles[ny][nx]) !== w0) { shoreTile = true; break; } } }
      let wet = kinds && shoreTile ? WET.has(kinds[py * (cols * TS) + x]) : WET.has(tiles[ty][tx]);
      /* AND THE MASK'S EDGE IS FRAYED, or the tile art's shoreline is wasted.
       *
       * This asks `WET.has(tiles[ty][tx])` -- one answer for a whole tile -- so
       * the region the shader paints has perfectly tile-quantised edges. The
       * tile art under it frays its shoreline into the bank over four pixels,
       * and none of that survives: the shader paints its flat water back over
       * the top out to the tile boundary. On a marsh the result is a
       * checkerboard of cyan and green rectangles, and it is why fraying the
       * art alone changed 22% of the pixels in a crop and looked identical.
       *
       * So the mask is eaten back the same way, on the sides that face dry
       * land, with the same falling odds. Hashed on the pixel so it is stable
       * frame to frame; the pattern does not have to agree with the art's own
       * dither, only to stop the edge being a ruled line. */
      if (wet) {
        const dryAt = (nx, ny) => (nx < 0 || ny < 0 || nx >= cols || ny >= rows)
          ? false : !WET.has(tiles[ny][nx]);
        const ix = x % TS, iy = py % TS;
        let near;
        if (kinds && shoreTile) {
          // distance to the nearest dry pixel along the four axes, up to four pixels
          near = 99;
          for (let k = 1; k <= 4 && near === 99; k++) {
            if ((x - k >= 0 && !WET.has(kindAt(x - k, py))) || (x + k < cols * TS && !WET.has(kindAt(x + k, py)))
              || (py - k >= 0 && !WET.has(kindAt(x, py - k))) || (py + k < rows * TS && !WET.has(kindAt(x, py + k)))) near = k - 1;
          }
        } else near = Math.min(
          dryAt(tx, ty - 1) ? iy : 99,
          dryAt(tx, ty + 1) ? TS - 1 - iy : 99,
          dryAt(tx - 1, ty) ? ix : 99,
          dryAt(tx + 1, ty) ? TS - 1 - ix : 99);
        if (near < 4) {
          const ODDS = [0.78, 0.52, 0.30, 0.14];
          let h = (x * 374761393 + y * 668265263) | 0;
          h = (h ^ (h >>> 13)) * 1274126177 | 0;
          const r = ((h ^ (h >>> 16)) >>> 0) / 4294967296;
          if (r < ODDS[near]) wet = false;
        }
      }
      depth = wet ? depth + 1 : -1;
      if (!wet) continue;
      /* THE OPEN SEA IS DEEP FROM ITS FIRST PIXEL (LS-383, the merge). The column walk is a lake's notion -- the water
       * shelves down from its top edge -- and the shader bands and lips it by that depth, so every all-sea screen wore
       * a pale lip along its top and wherever a column's walk began again; and the sea was written to the deep-or-
       * shallow channel as SHALLOW. The sea starts forty pixels deep and is deep. */
      const kindHere = kinds && shoreTile ? kindAt(x, py) : tiles[ty][tx];
      if (kindHere === T.SEA) depth = Math.max(depth, 40);
      any = true;
      const i = (y * W + x) * 4;
      d[i] = 255;
      d[i + 1] = Math.min(255, depth);
      d[i + 2] = kindHere === T.WATER || kindHere === T.SEA ? 255 : 96;   // deep or shallow
      // ...and how far down the HUD sits, so the shader can refuse to reflect
      // anything above the playfield
      d[i + 3] = 255;
    }
  }
  if (!any) return null;
  /* THE DEPTH RUNS ACROSS THE SHORE, NOT DOWN IT (LS-218). Depth is counted
   * down each column from where the water starts; a column the field made
   * wet a few rows lower than its neighbour carried a shallower count all
   * the way down, and the shader drew that step as a line at the old cell
   * edge. On the shore tiles the count is relaxed sideways: a wet pixel is at
   * least as deep as its wet neighbour less one, a few passes over, so the
   * band follows the meander instead of the cell. */
  if (kinds) {
    const W2 = W;
    for (let pass = 0; pass < 6; pass++) {
      for (let py = 0; py < rows * TS; py++) {
        const y = py + hudH;
        for (let x = 1; x < W2 - 1; x++) {
          const i = (y * W2 + x) * 4;
          if (!d[i + 3] || !d[i]) continue;
          const l = (y * W2 + x - 1) * 4, r = (y * W2 + x + 1) * 4;
          let best = d[i + 1];
          if (d[l] && d[l + 1] - 1 > best) best = d[l + 1] - 1;
          if (d[r] && d[r + 1] - 1 > best) best = d[r + 1] - 1;
          d[i + 1] = best;
        }
      }
    }
  }
  g.putImageData(img, 0, 0);
  return c;
}
